import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  emptyDryBulkNominationForm,
  type DryBulkNominationFormData,
} from "../schema/dryBulkNominationSchema";

export const DRY_BULK_NOMINATION_FORM_PERSIST_KEY = "dry-bulk-nomination-form";
export const NEW_DRAFT_KEY = "new";

/**
 * Unsaved dry-bulk form input (frontend guidelines § 5). One draft at a time: the store belongs to `draftKey`
 * ("new" or the draft id) and is reset when another draft is opened, after a save and after a discard (AC18).
 * `stale` is set when the server reported the draft changed elsewhere (409), so the next load takes the server copy.
 */
interface DryBulkNominationFormState {
  draftKey: string | null;
  vesselId: string | null;
  stale: boolean;
  values: Partial<DryBulkNominationFormData>;
  open: (draftKey: string, vesselId: string | null) => void;
  setAll: (values: Partial<DryBulkNominationFormData>) => void;
  markStale: () => void;
  resetForm: () => void;
}

const initialState = { draftKey: null, vesselId: null, stale: false, values: {} };

export const useDryBulkNominationFormStore = create<DryBulkNominationFormState>()(
  persist(
    (set, get) => ({
      ...initialState,
      open: (draftKey, vesselId) => {
        if (get().draftKey !== draftKey) {
          set({ draftKey, vesselId, stale: false, values: {} });
        } else if (vesselId) {
          set({ vesselId });
        }
      },
      setAll: (values) => set({ values }),
      markStale: () => set({ stale: true }),
      resetForm: () => set({ ...initialState, values: emptyDryBulkNominationForm() }),
    }),
    { name: DRY_BULK_NOMINATION_FORM_PERSIST_KEY },
  ),
);
